import { trackProductAction } from '@/libs/product-analytics';
import { toaster } from '@/components/ui/toaster';
import { useAuth } from '@/contexts/auth-context';
import api from '@/libs/axiosInstance';
import useLanguageStore from '@/stores/language-store';
import type { ApiResponse } from '@fluenceur/types';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

export interface UpdateUserSettingsDto {
  preferredLanguage?: 'en' | 'fr' | 'it';
}

export interface UserSettings {
  preferredLanguage?: 'en' | 'fr' | 'it';
}

export const useUpdateUserSettingsAction = () => {
  const { t } = useTranslation();
  const { setLanguage } = useLanguageStore();
  const { refreshCurrentUser } = useAuth();

  return useMutation<ApiResponse<UserSettings>, Error, UpdateUserSettingsDto>({
    mutationFn: async (data: UpdateUserSettingsDto) => {
      return trackProductAction('settings', 'update', async () => {
        const response = await api.patch('/dashboard/users/me/settings', data);
        return response.data;
      });
    },
    onSuccess: async (response) => {
      // Update language using the language store (which updates both i18n and store)
      if (response.data?.preferredLanguage) {
        setLanguage(response.data.preferredLanguage);
      }

      // Refresh user data from auth context to get updated settings
      await refreshCurrentUser();

      toaster.success({
        title: t('settings.success'),
        description: t('settings.languageUpdated'),
      });
    },
    onError: (_error: Error) => {
      toaster.error({
        title: t('common.error'),
        description: t('settings.languageUpdateFailed'),
      });
    },
  });
};
